import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import req from '@sportninja/common/api/request'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import { PlayerData } from '../types'

interface SportOption {
  value: string | undefined
  label: string
}

interface SportOptionGroup {
  label: string
  title: string
  options: SportOption[]
}

interface StatsRow {
  headers: string[]
  values: string[]
}

interface UseCareerStatsResult {
  selectedSport: string
  setSelectedSport: (id: string) => void
  selectOptions: SportOptionGroup[]
  filterOptionGroup: (search: string, option: any) => boolean
  playerStats: StatsRow | null
  goalieStats: StatsRow | null
  allPlayerStats: any[]
  allGoalieStats: any[]
  statsLoading: boolean
  isGenericSport: boolean
  sportName: string | null
}

export function useCareerStats(
  player: PlayerData | null,
  sports: any[],
  genericSports: any[]
): UseCareerStatsResult {
  const playerSports = useMemo(() => {
    if (!player?.teams) {
      return []
    }
    const mappedSports = player.teams.map((team) => {
      if (team.generic_sport_id) {
        const genericSport = genericSports.find(
          (s) => s.id === team.generic_sport_id
        )
        return {
          ...genericSport,
          sport_id: team.sport_id,
          is_generic: true,
          name_full: genericSport?.name,
        }
      }
      const lSport = sports.find((s) => s.id === team.sport_id)
      return {
        ...lSport,
        sport_id: team.sport_id,
        is_generic: false,
        name_full: lSport?.name_full,
      }
    })
    return mappedSports.filter(
      (sport, index, self) => index === self.findIndex((s) => s.id === sport.id)
    )
  }, [player, sports, genericSports])

  const [selectedSport, setSelectedSport] = useState(playerSports[0]?.id || '')

  // Keep the selected sport valid for the CURRENT player. When the player
  // changes, `selectedSport` still holds the previous player's sport id — if
  // that id isn't in the new player's sports, fall back to their first sport
  // (or clear it when they have none). Without this, switching players leaves a
  // stale sport id, `selSport` resolves to null, and the stats query never
  // fires (enabled: false).
  useEffect(() => {
    const stillValid = playerSports.some((s) => s.id === selectedSport)
    if (!stillValid) {
      setSelectedSport(playerSports[0]?.id || '')
    }
  }, [playerSports, selectedSport])

  const selectOptions = useMemo(() => {
    const fullStatsOptions = playerSports
      .filter((sport) => !sport.is_generic)
      .map((sport) => ({
        value: sport.id,
        label: sport.name_full || '',
      }))
      .sort((a, b) => a.label.localeCompare(b.label))

    const basicStatsOptions = playerSports
      .filter((sport) => sport.is_generic)
      .map((sport) => ({
        value: sport.id,
        label: sport.name_full || '',
      }))
      .sort((a, b) => a.label.localeCompare(b.label))

    const options: SportOptionGroup[] = []
    if (fullStatsOptions.length > 0) {
      options.push({
        label: 'Full Stats',
        title: 'fullStats',
        options: fullStatsOptions,
      })
    }
    if (basicStatsOptions.length > 0) {
      options.push({
        label: 'Basic Stats',
        title: 'basicStats',
        options: basicStatsOptions,
      })
    }
    return options
  }, [playerSports])

  const filterOptionGroup = useCallback(
    (search: string, optionOrGroup: any) => {
      if (Array.isArray(optionOrGroup.options)) {
        return false
      }
      return optionOrGroup.label.toLowerCase().includes(search.toLowerCase())
    },
    []
  )

  const isGenericSport = useMemo(() => {
    const sel = playerSports.find((sport) => sport.id === selectedSport)
    return sel?.is_generic || false
  }, [playerSports, selectedSport])

  const selectedSportObj = useMemo(
    () => playerSports.find((sport) => sport.sport_id === selectedSport),
    [playerSports, selectedSport]
  )

  const selSport = useMemo(
    () => playerSports.find((sport) => sport.id === selectedSport),
    [playerSports, selectedSport]
  )

  // The stats query only fires when we have a player and a sport from THAT
  // player's list (selSport). selectedSport is kept in sync above.
  const statsEnabled = Boolean(player?.id && selectedSport && selSport)

  // Fetch the skater + goalie season-stat lists for the selected sport via
  // React Query so switching tabs (which remounts this hook) reuses the cache
  // instead of re-hitting /stats/profile/list/v2 every time.
  const { data: careerStatsData, isPending: statsPending } = useQuery({
    queryKey: queryKeys.playerSeasonStats.byPlayerSport(
      player?.id,
      selSport?.sport_id,
      selSport?.is_generic ? selSport?.id : null,
      'both'
    ),
    enabled: statsEnabled,
    queryFn: async () => {
      const queryBase = {
        sort: 'desc',
        sortBy: 'points',
        sport_id: selSport!.sport_id,
        generic_sport_id: selSport!.is_generic ? selSport!.id : undefined,
      }

      const [playerResponse, goalieResponse] = await Promise.all([
        req(`/players/${player!.id}/stats/profile/list/v2`, {
          query: { ...queryBase, goalie: 0 },
        }).catch(() => []),
        req(`/players/${player!.id}/stats/profile/list/v2`, {
          query: { ...queryBase, goalie: 1 },
        }).catch(() => []),
      ])

      const filterStats = (response: any) => {
        const filtered = Array.isArray(response)
          ? response
              .filter((item) => typeof item === 'object')
              .filter((item) => item.sport_id === selSport!.sport_id)
          : []
        if (selSport!.is_generic) {
          return filtered.filter(
            (stat) => stat.generic_sport_id === selSport!.id
          )
        }
        return filtered
      }

      return {
        player: filterStats(playerResponse),
        goalie: filterStats(goalieResponse),
      }
    },
  })

  // Stable references so the summary useMemo()s below don't recompute every
  // render (a fresh `?? []` would be a new array each time).
  const allPlayerStats = useMemo(
    () => careerStatsData?.player ?? [],
    [careerStatsData]
  )
  const allGoalieStats = useMemo(
    () => careerStatsData?.goalie ?? [],
    [careerStatsData]
  )
  // While no sport is selectable yet there's nothing to load — don't show a
  // perpetual spinner. Otherwise mirror the query's pending state.
  const statsLoading = Boolean(selectedSport && selSport) && statsPending

  const calculateSummaryStats = useCallback(
    (stats: any[], statKeys: string[]) => {
      if (!stats?.length || !statKeys?.length) {
        return null
      }

      const summary: Record<string, number> = {}
      statKeys.forEach((key) => {
        summary[key] = 0
      })

      stats.forEach((record) => {
        statKeys.forEach((key) => {
          const statValue =
            record.stats?.find(
              (s: any) => s.type?.abbr === key || s.abbr === key
            )?.value || '0'
          if (!key.includes('%') && !key.includes('/')) {
            summary[key] += parseFloat(statValue) || 0
          }
        })
      })

      return summary
    },
    []
  )

  const getPlayerDisplayOrder = useCallback(() => {
    if (isGenericSport) {
      return ['GP', 'P']
    }
    if (selectedSportObj?.name === 'soccer') {
      return ['MP', 'G', 'A', 'FC', 'YC', 'RC', 'BC']
    }
    return ['GP', 'G', 'A', 'P', 'PiM', 'PPG']
  }, [isGenericSport, selectedSportObj])

  const getGoalieDisplayOrder = useCallback(() => {
    if (selectedSportObj?.name === 'soccer') {
      return ['MP', 'W', 'L', 'D', 'GA', 'SV', 'CS']
    }
    return ['GP', 'W', 'L', 'T', 'OTL', 'SO', 'MIN', 'PiM', 'SA', 'SV', 'GA']
  }, [selectedSportObj])

  const playerStatsResult = useMemo((): StatsRow | null => {
    const displayOrder = getPlayerDisplayOrder()
    const summary = calculateSummaryStats(allPlayerStats, displayOrder)
    if (!summary) {
      return null
    }
    return {
      headers: displayOrder,
      values: displayOrder.map((key) =>
        Number.isInteger(summary[key])
          ? String(summary[key])
          : String(Math.round(summary[key] * 100) / 100)
      ),
    }
  }, [allPlayerStats, getPlayerDisplayOrder, calculateSummaryStats])

  const goalieStatsResult = useMemo((): StatsRow | null => {
    if (isGenericSport) {
      return null
    }
    const displayOrder = getGoalieDisplayOrder()
    const summary = calculateSummaryStats(allGoalieStats, displayOrder)
    if (!summary) {
      return null
    }
    return {
      headers: displayOrder,
      values: displayOrder.map((key) =>
        Number.isInteger(summary[key])
          ? String(summary[key])
          : String(Math.round(summary[key] * 100) / 100)
      ),
    }
  }, [
    allGoalieStats,
    isGenericSport,
    getGoalieDisplayOrder,
    calculateSummaryStats,
  ])

  const sportName = useMemo(
    () => selectedSportObj?.name || null,
    [selectedSportObj]
  )

  return {
    selectedSport,
    setSelectedSport,
    selectOptions,
    filterOptionGroup,
    playerStats: playerStatsResult,
    goalieStats: goalieStatsResult,
    allPlayerStats,
    allGoalieStats,
    statsLoading,
    isGenericSport,
    sportName,
  }
}
